// AI Service for BabyXchange
// Handles AI-powered features like pricing advice, safety checks, and recommendations

export const aiService = {
  // Configuration
  config: {
    useOpenAI: import.meta.env.VITE_USE_OPENAI === 'true',
    openaiApiKey: import.meta.env.VITE_OPENAI_API_KEY,
    model: 'gpt-3.5-turbo',
    maxTokens: 500,
    temperature: 0.7
  },

  // System prompts for different AI functions
  prompts: {
    pricing: `You are a baby item pricing expert for BabyXchange marketplace. 
Help users determine fair prices for baby items based on:
- Item type, brand, and condition
- Age and wear patterns
- Market demand and seasonality
- Original retail price (if known)
- Local market conditions

Provide specific pricing ranges and reasoning. Be helpful and encouraging.`,

    safety: `You are a baby safety expert for BabyXchange marketplace.
Help parents verify the safety of baby items by checking:
- Age recommendations and suitability
- Recalls and safety warnings
- Proper usage guidelines
- Maintenance and inspection needs
- Replacement schedules (especially for car seats)

Always prioritize safety and recommend professional inspection when needed.`,

    recommendations: `You are a baby product recommendation specialist for BabyXchange marketplace.
Help parents find the right baby items based on:
- Baby's age and developmental stage
- Budget constraints and preferences
- Space limitations and lifestyle
- Essential vs. nice-to-have items
- Brand preferences and quality considerations

Provide personalized, practical recommendations with explanations.`,

    general: `You are a helpful AI assistant for BabyXchange, a baby item marketplace.
Help users with:
- Platform features and navigation
- Listing creation and management
- Safe transactions and meeting practices
- Community guidelines and best practices
- General baby item questions

Be friendly, informative, and always prioritize user safety and trust.`
  },

  // Detect the type of query to use appropriate prompt
  detectQueryType(userInput) {
    const input = userInput.toLowerCase();
    
    if (input.includes('price') || input.includes('cost') || input.includes('worth') || input.includes('value')) {
      return 'pricing';
    }
    
    if (input.includes('safe') || input.includes('safety') || input.includes('age') || input.includes('recall')) {
      return 'safety';
    }
    
    if (input.includes('recommend') || input.includes('need') || input.includes('suggest') || input.includes('essential')) {
      return 'recommendations';
    }
    
    return 'general';
  },

  // Generate AI response using OpenAI API (if configured) or fallback
  async generateResponse(userInput, context = {}) {
    try {
      const queryType = this.detectQueryType(userInput);
      const systemPrompt = this.prompts[queryType];

      if (this.config.useOpenAI && this.config.openaiApiKey) {
        return await this.callOpenAI(userInput, systemPrompt, context);
      } else {
        return await this.generateFallbackResponse(userInput, queryType, context);
      }
    } catch (error) {
      console.error('AI service error:', error);
      return this.getErrorMessage();
    }
  },

  // Call OpenAI API
  async callOpenAI(userInput, systemPrompt, context) {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.openaiApiKey}`
      },
      body: JSON.stringify({
        model: this.config.model,
        messages: [
          { role: 'system', content: systemPrompt },
          { 
            role: 'user', 
            content: this.buildContextualPrompt(userInput, context)
          }
        ],
        max_tokens: this.config.maxTokens,
        temperature: this.config.temperature
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    return data.choices[0].message.content.trim();
  },

  // Build contextual prompt with marketplace data
  buildContextualPrompt(userInput, context) {
    let prompt = userInput;

    if (context.listing) {
      prompt += `\n\nContext about the item:\n`;
      prompt += `- Title: ${context.listing.title}\n`;
      prompt += `- Category: ${context.listing.category}\n`;
      prompt += `- Condition: ${context.listing.condition}\n`;
      if (context.listing.description) {
        prompt += `- Description: ${context.listing.description}\n`;
      }
    }

    if (context.user) {
      prompt += `\n\nUser context:\n`;
      if (context.user.babyAge) {
        prompt += `- Baby's age: ${context.user.babyAge}\n`;
      }
      if (context.user.location) {
        prompt += `- Location: ${context.user.location}\n`;
      }
    }

    return prompt;
  },

  // Fallback responses when OpenAI is not available
  async generateFallbackResponse(userInput, queryType, context) {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const responses = {
      pricing: this.getPricingFallback(userInput, context),
      safety: this.getSafetyFallback(userInput, context),
      recommendations: this.getRecommendationsFallback(userInput, context),
      general: this.getGeneralFallback(userInput, context)
    };

    return responses[queryType] || responses.general;
  },

  getPricingFallback(userInput, context) {
    return `💰 **Pricing Guidance**

Based on your question about pricing, here's what I can help with:

**Key factors for fair pricing:**
• **Condition:** New (70-90% retail), Like-new (60-80%), Good (40-60%), Fair (20-40%)
• **Brand reputation:** Premium brands hold value better
• **Seasonality:** Strollers sell better in spring/summer
• **Local demand:** Check similar items in your area
• **Included accessories:** Add value to your listing

**Pro tips:**
• Take clear photos showing condition
• Be honest about wear and tear
• Include original purchase price if known
• Price competitively but not too low

**Need specific help?** Tell me:
- What item you're selling
- Brand and age
- Current condition
- Original price (if known)

I'll give you a personalized pricing range! 🎯`;
  },

  getSafetyFallback(userInput, context) {
    return `🛡️ **Safety Check**

I'm here to help ensure your baby items are safe! Here's what I can assist with:

**Critical safety checks:**
• **Car seats:** Check expiration dates (6-10 years max)
• **Cribs:** Ensure no recalls, proper assembly
• **Toys:** Age-appropriate, no small parts
• **Strollers:** All locks and brakes working
• **High chairs:** Stable base, working harness

**Red flags to watch for:**
• Missing safety labels or instructions
• Visible damage or wear
• Recalled items (check CPSC.gov)
• Items past their safety expiration
• Missing safety features

**Resources I recommend:**
• CPSC.gov for recall information
• Manufacturer websites for age limits
• Our community reviews for real experiences

**What specific item** would you like me to help you check for safety? I'll guide you through the key things to verify! 👶`;
  },

  getRecommendationsFallback(userInput, context) {
    return `🎯 **Personalized Recommendations**

I'd love to help you find the perfect baby items! To give you the best suggestions, tell me:

**Essential information:**
• Baby's current age/stage
• Your budget range
• Must-haves vs. nice-to-haves
• Space constraints
• Brand preferences

**Popular essentials by stage:**
• **Newborn (0-3m):** Car seat, stroller, crib, baby monitor, swaddles
• **Infant (3-12m):** High chair, play mat, baby carrier, teething toys
• **Toddler (1-3y):** Convertible car seat, booster chair, ride-on toys
• **Preschooler (3-5y):** Balance bike, art supplies, educational toys

**Budget-friendly tips:**
• Buy used for items used briefly (infant car seats, newborn clothes)
• Invest in items that grow with baby (convertible car seats, adjustable high chairs)
• Check our marketplace for great deals!

**What stage is your little one at?** I'll customize my recommendations! 👶✨`;
  },

  getGeneralFallback(userInput, context) {
    return `🤖 **AI Assistant Help**

I understand you're asking about: "${userInput}"

I'm here to help with all things BabyXchange! Here's what I can assist with:

**💰 Pricing Help**
• Fair pricing for your items
• Market value assessment
• Pricing strategies

**🛡️ Safety Checks**
• Age recommendations
• Recall information
• Safety guidelines

**🎯 Recommendations**
• What items you need
• Best brands and models
• Budget-friendly options

**📱 Platform Help**
• Using marketplace features
• Creating great listings
• Safe transactions

**Could you be more specific** about what you need help with? I'm here to make your marketplace experience amazing! 😊`;
  },

  getErrorMessage() {
    return `⚠️ **Temporary Issue**

I'm experiencing some technical difficulties right now. Please try asking your question again in a moment.

**In the meantime, you can:**
• Browse our marketplace for similar items
• Check our FAQ section
• Contact our support team
• Use the quick action buttons below

I'll be back online shortly! 🤖✨`;
  },

  // Specialized AI functions for specific use cases
  async getPricingAdvice(itemDetails) {
    const prompt = `Analyze this baby item and provide pricing advice: ${JSON.stringify(itemDetails)}`;
    return await this.generateResponse(prompt, { listing: itemDetails });
  },

  async checkSafety(itemDetails, babyAge) {
    const prompt = `Check if this item is safe for a ${babyAge} year old: ${JSON.stringify(itemDetails)}`;
    return await this.generateResponse(prompt, { 
      listing: itemDetails, 
      user: { babyAge } 
    });
  },

  async getRecommendations(babyStage, budget, preferences) {
    const prompt = `Recommend baby items for ${babyStage} with budget ${budget} and preferences: ${preferences}`;
    return await this.generateResponse(prompt, { 
      user: { babyAge: babyStage, budget, preferences } 
    });
  },

  // Analytics and tracking
  trackAIInteraction(queryType, userInput, responseTime) {
    // Track AI usage for analytics
    console.log('AI interaction tracked:', {
      type: queryType,
      input: userInput.substring(0, 100), // Truncate for privacy
      responseTime,
      timestamp: new Date().toISOString()
    });

    // Send to analytics service if available
    if (typeof gtag !== 'undefined') {
      gtag('event', 'ai_interaction', {
        'event_category': 'ai_assistant',
        'event_label': queryType,
        'value': responseTime
      });
    }
  }
};

// Environment variables needed:
/*
VITE_USE_OPENAI=true
VITE_OPENAI_API_KEY=your_openai_api_key_here
*/
